export const FINCORE_API_KEY = 'FINCORE';

export interface FincoreError {
  code: number;
  message: string;
  detail: string;
}

export interface FincoreResponseEnvelope {
  errors?: FincoreError[];
  status?: number | string;
  summary?: string;
}

export interface BFeeGroupOption {
  id: number;
  bfgrno: number;
  bfggid: number;
  label: string;
}

export interface ReferenceDateRequest {
  referenceDate: string;
}

// See C002 - 2026-08-21 - HE
export interface RunBatchBfeeGroupRequest {
  bfeeGroupId: number;
  referenceDate: string;
}

export interface RunBatchResponse extends FincoreResponseEnvelope {
  rowsAffected: number;
  calendarMonth: string;
}

export interface AccountsSelectResponse extends FincoreResponseEnvelope {
  rowCount: number;
  calendarMonth: string;
  emailSent: boolean;
}

export interface RowsAffectedResponse extends FincoreResponseEnvelope {
  rowsAffected: number;
  emailSent: boolean;
}

export type FincoreFeeProduct = 'WIZZIT' | 'SURECARD';
export type FincoreFeeAction = 'SELECT' | 'UPDATE';

export interface FeeManagementRequest {
  product: FincoreFeeProduct;
  action: FincoreFeeAction;
  referenceDate: string;
}

export interface FeeManagementResponse extends FincoreResponseEnvelope {
  product: string;
  action: string;
  referenceDate: string;
  calendarMonth: string;
  rowCount: number;
  emailSent: boolean;
  message: string;
}

export const FINCORE_ATA_STATUSES = [
  'To be Authorized',
  'Authorized',
  'Expired',
  'Rejected',
  'Cancelled',
  'Manually Processed',
  'Waiting for New Merchant Acc',
  'Completed',
  'Reversed',
  'Reserved',
  'Reservation To Auth',
] as const;

export type FincoreAtaStatus = (typeof FINCORE_ATA_STATUSES)[number];

export interface UpdateAtaStatusRequest {
  // FinCore ATA id is Long (not 32-bit int) 28/08/2026 DW
  id?: number | string;
  ids?: Array<number | string>;
  status: FincoreAtaStatus | string | number;
  // Omitted from update-ata-status payload 09/09/2026 DW
  reason?: string;
}

export interface UpdateAtaStatusResponse extends FincoreResponseEnvelope {
  ids?: Array<number | string>;
  status?: string;
  reason?: string;
  success?: boolean;
}

export interface CreditRejectionRequest {
  bankAccountNo: string;
}

export interface ResendSmsRequest {
  id: number | string;
}

// ========================================
// CHANGE LOG
// ========================================
// C001: Added FinCore REST contract models for monthly-fee and account-maintenance APIs. Previous behavior: No TypeScript types existed for FinCore (BFeeGroupOption, run-batch, unified accounts, ATA status, credit rejection, resend SMS). New behavior: Models cover request/response payloads plus optional errors/status/summary envelope used by portaladmin API docs, yyyyMMdd date strings, WIZZIT/SURECARD product and SELECT/UPDATE action unions, and FINCORE_ATA_STATUSES labels. Reason: Portaladmin needs typed HttpClient calls for FinCore REST API V1.000 without UI. Context: Integration to FinCore; monthly-fees and account-maintenance resource groups. Impact: Consumed by FincoreMonthlyFeeService and FincoreAccountMaintenanceService; no runtime behavior. - 2026-08-20 - HE
// C002: Documented RunBatchBfeeGroupRequest / RunBatchResponse as the official V1.000 "Fee Management - Selected BFeeGroup" contract. Previous behavior: Types existed from the FinCore UI catalogue but were not called out as a first-class spec resource. New behavior: Request is { bfeeGroupId: number, referenceDate: yyyyMMdd }; 200 response is envelope plus rowsAffected and calendarMonth (first of month). Reason: Spec addition 2026-08-21. Context: POST /api/fincore/monthly-fees/run-batch-bfee-group. Impact: No field change; same types now match the published resource. - 2026-08-21 - HE
// C003: Typed UpdateAtaStatusRequest.id / ids as number | string (FinCore Long). Previous behavior: id/ids were number only, implying 32-bit int. New behavior: Accepts number or digit string so full Long values serialize without forced int casting. Reason: update-ata-status id is Long on the API. Context: FinCore account-maintenance. Impact: Callers may pass large IDs as string when outside Number.MAX_SAFE_INTEGER. - 2026-08-28 - DW
